import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import { SUPPORTED_LANGUAGES } from '../../i18n/languages';
import { loadRegionalConfig, localizedCountryName } from '../../utils/regionalConfig';

const GROUP_ORDER = ['RU', 'AM', 'EU', 'EEA', 'CIS', 'OTHER'];
const COLLAPSED_BY_DEFAULT = new Set(['OTHER']);

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const cardStyle = { marginBottom: 16, padding: 16, background: '#f8fafc', borderRadius: 12 };
const chipRow = { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px 16px' };
const checkLabel = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' };

export default function RegionalAvailabilityPage() {
    const { t, i18n } = useTranslation(['adminRegional', 'common']);
    const [countries, setCountries] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [defaults, setDefaults] = useState({ citizenships: [], languages: [] });
    const [enabledCountries, setEnabledCountries] = useState(new Set());
    const [enabledLanguages, setEnabledLanguages] = useState(new Set());
    const [saved, setSaved] = useState({ countries: '', languages: '' });
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgIsError, setMsgIsError] = useState(false);

    const applyView = useCallback((data) => {
        const on = new Set(data.citizenships.filter(c => c.enabled).map(c => c.code));
        const langOn = new Set(data.languages.filter(l => l.enabled).map(l => l.code));
        setCountries(data.citizenships);
        setLanguages(data.languages);
        setDefaults({ citizenships: data.defaultCitizenships, languages: data.defaultLanguages });
        setEnabledCountries(on);
        setEnabledLanguages(langOn);
        setSaved({ countries: [...on].sort().join(','), languages: [...langOn].sort().join(',') });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await adminApi().get('/admin/regional-availability');
            applyView(res.data);
        } catch {
            setMsg(t('adminRegional:loadError'));
            setMsgIsError(true);
        }
        setLoading(false);
    }, [applyView, t]);

    useEffect(() => { load(); }, [load]);

    const dirty = saved.countries !== [...enabledCountries].sort().join(',')
        || saved.languages !== [...enabledLanguages].sort().join(',');

    const name = useCallback((c) => localizedCountryName(c, i18n.language), [i18n.language]);

    const grouped = useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = (c) => !q
            || c.code.toLowerCase() === q
            || c.nameRu.toLowerCase().includes(q)
            || c.nameEn.toLowerCase().includes(q);
        const map = new Map(GROUP_ORDER.map(g => [g, []]));
        countries.filter(matches).forEach(c => (map.get(c.group) ?? map.get('OTHER')).push(c));
        map.forEach(list => list.sort((a, b) => name(a).localeCompare(name(b), i18n.language)));
        return map;
    }, [countries, search, name, i18n.language]);

    function toggleCountry(code) {
        setEnabledCountries(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    }

    function setGroup(list, on) {
        setEnabledCountries(prev => {
            const next = new Set(prev);
            list.forEach(c => (on ? next.add(c.code) : next.delete(c.code)));
            return next;
        });
    }

    function toggleLanguage(code) {
        setEnabledLanguages(prev => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    }

    async function save() {
        if (enabledCountries.size === 0) {
            setMsg(t('adminRegional:noCitizenships'));
            setMsgIsError(true);
            return;
        }
        setSaving(true);
        setMsg('');
        setMsgIsError(false);
        try {
            const res = await adminApi().put('/admin/regional-availability', {
                citizenships: [...enabledCountries],
                languages: [...enabledLanguages],
            });
            applyView(res.data);
            setMsg(t('adminRegional:saved'));
            void loadRegionalConfig(); // обновить кэш этого же браузера
        } catch (err) {
            setMsg(err.response?.data?.message || t('adminRegional:saveError'));
            setMsgIsError(true);
        }
        setSaving(false);
    }

    const langInfo = (code) => SUPPORTED_LANGUAGES.find(l => l.code === code);

    if (loading) {
        return (
            <OwnerPageLayout title={t('adminRegional:pageTitle')} maxWidth={900}>
                <div className="sp-loading-page">{t('common:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout title={t('adminRegional:pageTitle')} maxWidth={900}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{t('adminRegional:subtitle')}</p>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{t('adminRegional:perServerNote')}</p>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{t('adminRegional:existingNote')}</p>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{t('adminRegional:ruNote')}</p>

            {msg && (
                <p style={{
                    marginBottom: 12, fontSize: 13, padding: '10px 14px', borderRadius: 8,
                    background: msgIsError ? '#fdecea' : '#e8f5e9',
                    color: msgIsError ? '#c0392b' : '#2e7d32',
                }}>
                    {msg}
                </p>
            )}

            <div className="sp-panel" style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 4px' }}>{t('adminRegional:citizenships')}</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>
                    {t('adminRegional:citizenshipsSummary', { enabled: enabledCountries.size, total: countries.length })}
                </p>
                <div style={chipRow}>
                    <input
                        className="sp-input"
                        style={{ maxWidth: 260 }}
                        value={search}
                        placeholder={t('adminRegional:search')}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <button
                        type="button"
                        className="sp-btn sp-btn--ghost"
                        onClick={() => setEnabledCountries(new Set(defaults.citizenships))}
                    >
                        {t('adminRegional:presetStart')}
                    </button>
                </div>

                {GROUP_ORDER.map(g => {
                    const list = grouped.get(g) ?? [];
                    if (list.length === 0) return null;
                    const allInGroup = countries.filter(c => c.group === g);
                    const onCount = allInGroup.filter(c => enabledCountries.has(c.code)).length;
                    return (
                        <details
                            key={g}
                            style={cardStyle}
                            open={!COLLAPSED_BY_DEFAULT.has(g) || !!search.trim()}
                        >
                            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                                {t(`adminRegional:groups.${g}`)}
                                <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                                    {t('adminRegional:groupCount', { enabled: onCount, total: allInGroup.length })}
                                </span>
                            </summary>
                            <div style={{ ...chipRow, marginTop: 10 }}>
                                <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setGroup(list, true)}>
                                    {t('adminRegional:enableAll')}
                                </button>
                                <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setGroup(list, false)}>
                                    {t('adminRegional:disableAll')}
                                </button>
                            </div>
                            <div style={gridStyle}>
                                {list.map(c => (
                                    <label key={c.code} style={checkLabel}>
                                        <input
                                            type="checkbox"
                                            checked={enabledCountries.has(c.code)}
                                            onChange={() => toggleCountry(c.code)}
                                        />
                                        <span>{c.flag} {name(c)}</span>
                                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{c.code}</span>
                                    </label>
                                ))}
                            </div>
                        </details>
                    );
                })}
                {[...grouped.values()].every(l => l.length === 0) && (
                    <p style={{ fontSize: 13, color: '#64748b' }}>{t('adminRegional:noResults')}</p>
                )}
            </div>

            <div className="sp-panel" style={{ marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 4px' }}>{t('adminRegional:languages')}</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px' }}>{t('adminRegional:languagesHint')}</p>
                <div style={chipRow}>
                    <button
                        type="button"
                        className="sp-btn sp-btn--ghost"
                        onClick={() => setEnabledLanguages(new Set(defaults.languages))}
                    >
                        {t('adminRegional:presetLanguages')}
                    </button>
                </div>
                <div style={gridStyle}>
                    {languages.map(l => {
                        const info = langInfo(l.code);
                        return (
                            <label key={l.code} style={{ ...checkLabel, cursor: l.locked ? 'default' : 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={enabledLanguages.has(l.code)}
                                    disabled={l.locked}
                                    onChange={() => toggleLanguage(l.code)}
                                />
                                <span>{info?.nativeLabel || l.code}</span>
                                <span style={{ color: '#94a3b8', fontSize: 12 }}>
                                    {l.code}{l.locked ? ` · ${t('adminRegional:locked')}` : ''}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" className="sp-btn sp-btn--primary" disabled={saving || !dirty} onClick={save}>
                    {saving ? t('adminRegional:saving') : t('adminRegional:save')}
                </button>
                {dirty && <span style={{ fontSize: 13, color: '#b45309' }}>{t('adminRegional:unsaved')}</span>}
            </div>
        </OwnerPageLayout>
    );
}
